import { Box, Link, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { FORTA_WHITE } from '../../../../../utils/colors';
import { FCheckbox } from '../../../../base';

import type { ParentCareGiverGoal } from './TargetSelectModalContent';

interface TargetSelectRowProps {
  goal: ParentCareGiverGoal;
  isEditable: boolean;
  savedTargetIDs: number[];
  selectedTargetIDs: number[];
  onAdd: (targetIds: number[]) => void;
  onRemove: (targetIds: number[]) => void;
}

function TargetSelectRow({
  goal,
  isEditable,
  savedTargetIDs,
  selectedTargetIDs,
  onAdd,
  onRemove,
}: TargetSelectRowProps) {
  const { goalName, targets } = goal;

  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

  // Find targets which can be selected
  const targetsToAdd = useMemo(() => {
    if (!savedTargetIDs) return targets;
    return targets.filter((target) => !savedTargetIDs.includes(target.targetId));
  }, [savedTargetIDs]);

  // Track and change the Add all / Remove all link based on selections
  useEffect(() => {
    if (!targetsToAdd.length) return;
    const areAllTargetsSelected = targetsToAdd.every((target) =>
      selectedTargetIDs.includes(target.targetId),
    );
    if (areAllTargetsSelected && !isAllSelected) {
      setIsAllSelected(true);
    }
    if (!areAllTargetsSelected && isAllSelected) {
      setIsAllSelected(false);
    }
  }, [selectedTargetIDs, targetsToAdd]);

  // Do not render add goal targets rowm when all targets have already been added
  if (targetsToAdd.length === 0) return null;

  const handleTargetAddRemove = (targetId: number, checked: boolean) => {
    if (checked) {
      onAdd([targetId]);
    } else {
      onRemove([targetId]);
    }
  };

  const handleSelectAll = () => {
    if (!isEditable) return;
    if (!isAllSelected) {
      onAdd(targetsToAdd.map((target) => target.targetId));
    } else {
      onRemove(targetsToAdd.map((target) => target.targetId));
    }
    setIsAllSelected((prev) => !prev);
  };

  return (
    <Box
      sx={{
        borderRadius: '8px',
        backgroundColor: FORTA_WHITE,
        p: 2,
        mt: 2,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography mb={1.5} ml={1}>
          {goalName}
        </Typography>
        {targetsToAdd.length > 1 && (
          <Link fontSize={14} onClick={handleSelectAll}>
            {isAllSelected ? 'Unselect all' : 'Select all'}
          </Link>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {targetsToAdd.map((target) => (
          <Box sx={{ my: 1, ml: 2 }} key={target.targetId}>
            <FCheckbox
              disabled={!isEditable}
              coloredBackground
              iconStyles={{ boxShadow: 'none', width: '25px', height: '25px' }}
              label={`${target.targetLabel}`}
              checked={selectedTargetIDs.includes(target.targetId)}
              onChange={(e) => handleTargetAddRemove(target.targetId, e.target.checked)}
              sx={{ width: 'fit-content' }}
              data-testid={`addButtonTarget_${target.targetLabel}`}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default TargetSelectRow;
